import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { normalizeHandle } from "../utils/handle";
import "./css/AuthModal.css";

export default function AuthModal({ onClose }) {
  const {
    currentUser,
    profile,
    signupEmail,
    loginEmail,
    signupOrLoginWithGoogle,
    sendPhoneOtp,
    verifyPhoneOtp,
    createPublicProfileWithHandle,
    checkHandleAvailabilityCallable,
    refreshProfile,
    logout,
  } = useAuth();

  // Auth form state
  const [section, setSection] = useState("email"); // "email" | "phone"
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  // Handle step state
  const [showHandleStep, setShowHandleStep] = useState(false);
  const [handleInput, setHandleInput] = useState("");
  const [handleNormalized, setHandleNormalized] = useState("");
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);

  // Misc
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDebounceRef = useRef(null);

  // refs for accessibility & focus management
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear?.();
          window.recaptchaVerifier = null;
        } catch (e) { }
      }
    };
  }, []);

  // -----------------------
  // IMPORTANT CHANGE:
  // Remove auto-show on mount; only open afterAuthSuccess or based on refreshProfile
  // -----------------------

  // Normalize & debounce availability check (unchanged but we disallow Save until available)
  useEffect(() => {
    const n = normalizeHandle(handleInput || "");
    setHandleNormalized(n);
    setHandleAvailable(null);

    if (!n) {
      setCheckingHandle(false);
      if (handleDebounceRef.current) {
        clearTimeout(handleDebounceRef.current);
        handleDebounceRef.current = null;
      }
      return;
    }

    setCheckingHandle(true);
    if (handleDebounceRef.current) clearTimeout(handleDebounceRef.current);
    handleDebounceRef.current = setTimeout(async () => {
      try {
        if (typeof checkHandleAvailabilityCallable === "function") {
          const res = await checkHandleAvailabilityCallable(n);
          setHandleAvailable(Boolean(res?.available));
        } else {
          // Conservative fallback: treat as unavailable
          setHandleAvailable(false);
        }
      } catch (e) {
        console.warn("[AuthModal] handle availability check failed", e);
        setHandleAvailable(false);
      } finally {
        setCheckingHandle(false);
      }
    }, 350);

    return () => {
      if (handleDebounceRef.current) clearTimeout(handleDebounceRef.current);
    };
  }, [handleInput, checkHandleAvailabilityCallable]);

  const close = () => {
    setError("");
    // Preserve signup fields so user can retry quickly; only clear OTP & handle step state when fully closing
    setOtpCode("");
    setOtpSent(false);
    setShowHandleStep(false);
    setHandleInput("");
    setHandleNormalized("");
    setHandleAvailable(null);
    if (onClose) onClose();
  };

  // Focus trapping and Escape handling
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
      if (e.key === "Tab") {
        // basic focus trap
        const focusable = dialogRef.current?.querySelectorAll(
          'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // -----------------------
  // afterAuthSuccess: central place to decide whether to show handle step
  // -----------------------
  async function afterAuthSuccess(userCredential, displayName) {
    const isNew = !!userCredential?.additionalUserInfo?.isNewUser;

    // If the SDK says this is a new user, always prompt for handle (reasonable UX)
    if (isNew) {
      setShowHandleStep(true);
      setHandleInput(displayName ? displayName.split(" ")[0] : "");
      return;
    }

    // Defensive refresh
    if (typeof refreshProfile === "function") {
      try {
        const refreshed = await refreshProfile();
        if (refreshed && !refreshed.handle) {
          setShowHandleStep(true);
          setHandleInput(displayName ? displayName.split(" ")[0] : "");
          return;
        }
        close();
        return;
      } catch (e) {
        console.warn("[AuthModal] refreshProfile failed in afterAuthSuccess", e);
        setShowHandleStep(true);
        setHandleInput(displayName ? displayName.split(" ")[0] : "");
        return;
      }
    }

    // fallback
    if (currentUser && profile && !profile.handle) {
      setShowHandleStep(true);
      setHandleInput(displayName ? displayName.split(" ")[0] : "");
    } else {
      close();
    }
  }

  // Email submit
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await loginEmail(email, password);
        close();
      } else {
        const cred = await signupEmail(email, password, name);
        const syntheticCred = { ...cred, additionalUserInfo: { isNewUser: true } };
        await afterAuthSuccess(syntheticCred, name);
      }
    } catch (err) {
      console.error("[AuthModal] email auth error", err);
      setError(err?.message || "Auth error — please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  // Google
  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await signupOrLoginWithGoogle();
      await afterAuthSuccess(result, result.user?.displayName ?? name);
    } catch (err) {
      console.error("[AuthModal] Google sign-in error", err);
      setError(err?.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  // Phone - send OTP
  const handleSendOtp = async () => {
    setError("");
    if (!phone) {
      setError("Enter phone in E.164 format (e.g. +919999888777)");
      return;
    }
    setLoading(true);
    try {
      await sendPhoneOtp(phone, "recaptcha-container");
      setOtpSent(true);
    } catch (err) {
      console.error("[AuthModal] sendPhoneOtp failed", err);
      setError(err?.message || "Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Phone - verify OTP
  const handleVerifyOtp = async () => {
    setError("");
    if (!otpCode) {
      setError("Enter the OTP code");
      return;
    }
    setLoading(true);
    try {
      const result = await verifyPhoneOtp(otpCode);
      await afterAuthSuccess(result, result.user?.displayName ?? "");
    } catch (err) {
      console.error("[AuthModal] verifyPhoneOtp failed", err);
      setError(err?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  // generate a few handle suggestions
  const generateHandleSuggestions = () => {
    const base = normalizeHandle(name || handleInput || "you") || "user";
    const suggestions = [];
    for (let i = 0; i < 4; i++) {
      const suffix = i === 0 ? "" : Math.floor(Math.random() * 900 + 100).toString();
      suggestions.push((base + suffix).slice(0, 30));
    }
    return suggestions;
  };

  // Claim handle
  const handleClaimHandle = async () => {
    setError("");
    const normalized = normalizeHandle(handleInput || "");
    if (!normalized) {
      setError("Please enter a valid handle");
      return;
    }

    setLoading(true);
    try {
      const res = await createPublicProfileWithHandle(normalized, name || undefined);

      // Refresh profile so popup won't reappear on next login
      if (typeof refreshProfile === "function") {
        try {
          await refreshProfile();
        } catch (refreshErr) {
          console.warn("[AuthModal] refreshProfile failed after claiming handle", refreshErr);
        }
      }

      setShowHandleStep(false);
      close();

      // copy to clipboard for convenience (best effort)
      try {
        await navigator.clipboard.writeText(`@${res.handle}`);
      } catch (err) {
        // ignore if not allowed
      }

      window.location.href = `/@${res.handle}`;
    } catch (err) {
      console.error("[AuthModal] createPublicProfileWithHandle failed", err);
      if (err?.code === "already-exists") {
        if (profile?.handle && profile.handle === normalized) {
          // idempotent success
          setShowHandleStep(false);
          close();
          window.location.href = `/@${normalized}`;
        } else if (profile?.handle && profile.handle !== normalized) {
          setError("You already have a different handle associated with your account.");
        } else {
          setError("That handle is already taken — please try another.");
        }
      } else if (err?.code === "permission-denied") {
        setError("You must be signed in to claim a handle.");
      } else if (err?.code === "invalid-argument") {
        setError("Invalid handle. Use letters, numbers, hyphens or underscores.");
      } else {
        setError(err?.message || "Could not claim handle. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const skipHandleStep = () => {
    setShowHandleStep(false);
    close();
  };

  // copy handle helper
  const copyHandle = async () => {
    if (!handleNormalized) return;
    try {
      await navigator.clipboard.writeText(`@${handleNormalized}`);
    } catch (e) {
      // fallback: select and let user copy
      setError("Copy not allowed by browser — please copy manually.");
    }
  };

  // overlay click: be careful not to close when user is mid-handle-claim
  const onOverlayMouseDown = (e) => {
    // if click outside dialog, close only when not in critical step or confirmation
    if (!dialogRef.current) return;
    if (!dialogRef.current.contains(e.target)) {
      // if currently entering handle and has unsaved input, don't close — treat as friendly guard
      if (showHandleStep && (handleInput || checkingHandle || loading)) {
        setError("Press 'Skip for now' or save your handle before closing.");
        return;
      }
      close();
    }
  };

  return (
    <div
      className="auth-overlay"
      onMouseDown={onOverlayMouseDown}
    >
      <div
        className="auth-container"
        onMouseDown={(e) => e.stopPropagation()}
        ref={dialogRef}
      >
        <button className="auth-close" onClick={close}>
          &times;
        </button>

        {/* HEADER */}
        <div className="auth-header">
          <h2>
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p>
            {mode === "login"
              ? "Sign in to continue your journey."
              : "Start sharing your journeys today."}
          </p>
        </div>

        {/* ================= HANDLE STEP ================= */}
        {showHandleStep ? (
          <div className="auth-body">
            <input
              placeholder="Choose your public handle"
              value={handleInput}
              onChange={(e) => setHandleInput(e.target.value)}
            />

            {checkingHandle && (
              <div className="info">Checking availability...</div>
            )}

            {handleNormalized && !checkingHandle && (
              <div
                className={
                  handleAvailable ? "success-text" : "error-text"
                }
              >
                {handleAvailable
                  ? `@${handleNormalized} is available`
                  : `@${handleNormalized} is taken`}
              </div>
            )}

            {error && <div className="error-box">{error}</div>}

            <button
              className="btn primary full"
              onClick={handleClaimHandle}
              disabled={!handleAvailable || loading}
            >
              Save handle
            </button>

            <button
              className="btn subtle full"
              onClick={skipHandleStep}
            >
              Skip for now
            </button>
          </div>
        ) : (
          <>
            {/* ================= EMAIL FLOW ================= */}
            {section === "email" ? (
              <>
                <form
                  className="auth-body"
                  onSubmit={handleEmailSubmit}
                >
                  {mode === "signup" && (
                    <input
                      placeholder="Full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  )}

                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />

                  {error && (
                    <div className="error-box">{error}</div>
                  )}

                  <button
                    type="submit"
                    className="btn primary full"
                  >
                    {mode === "login"
                      ? "Login"
                      : "Create account"}
                  </button>
                </form>

                <div className="divider">
                  <span>OR</span>
                </div>

                <div className="social-login">
                  <button
                    type="button"
                    className="google-pill"
                    onClick={handleGoogle}
                  >
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt=""
                      className="google-icon"
                    />
                    <span>Continue with Google</span>
                  </button>
                </div>


                <div className="auth-switch">
                  {mode === "login" ? (
                    <>
                      Don’t have an account?{" "}
                      <button
                        onClick={() => setMode("signup")}
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button
                        onClick={() => setMode("login")}
                      >
                        Login
                      </button>
                    </>
                  )}
                </div>

                <div className="phone-link">
                  <button
                    onClick={() => setSection("phone")}
                  >
                    Use phone instead
                  </button>
                </div>
              </>
            ) : (
              /* ================= PHONE FLOW ================= */
              <div className="auth-body">
                {!otpSent ? (
                  <>
                    <input
                      placeholder="Phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />

                    {error && (
                      <div className="error-box">{error}</div>
                    )}

                    <button
                      className="btn primary full"
                      onClick={handleSendOtp}
                    >
                      Send OTP
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      placeholder="Enter OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                    />

                    <button
                      className="btn primary full"
                      onClick={handleVerifyOtp}
                    >
                      Verify OTP
                    </button>
                  </>
                )}

                <div className="phone-link">
                  <button
                    onClick={() => setSection("email")}
                  >
                    Use email instead
                  </button>
                </div>

                <div id="recaptcha-container" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );


}
